import {
  compareR,
  equalsR,
  formatFraction,
  formatStyled,
  type NumberStyle,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * Solution sets of one-variable inequalities as unions of intervals on the real line.
 *
 * An end of `null` is infinite (and always open). Sets are kept normalised: no empty
 * intervals, sorted, and neighbours that overlap or touch with a closed end merged, so
 * (−∞, 2) ∪ [2, 5] is written (−∞, 5] while (−∞, 2) ∪ (2, 5] keeps its gap.
 */
export interface Bound {
  value: Rational;
  closed: boolean;
}

export interface Interval {
  lo: Bound | null;
  hi: Bound | null;
}

export type IntervalSet = Interval[];

export type Relation = "=" | "<" | "≤" | ">" | "≥";

export function flipRelation(rel: Relation): Relation {
  switch (rel) {
    case "<":
      return ">";
    case ">":
      return "<";
    case "≤":
      return "≥";
    case "≥":
      return "≤";
    default:
      return "=";
  }
}

export const ALL_REALS: IntervalSet = [{ lo: null, hi: null }];
export const NO_REALS: IntervalSet = [];

/** The set of x with `x rel c`. */
export function rayFor(rel: Relation, c: Rational): IntervalSet {
  switch (rel) {
    case "<":
      return [{ lo: null, hi: { value: c, closed: false } }];
    case "≤":
      return [{ lo: null, hi: { value: c, closed: true } }];
    case ">":
      return [{ lo: { value: c, closed: false }, hi: null }];
    case "≥":
      return [{ lo: { value: c, closed: true }, hi: null }];
    default:
      return [
        { lo: { value: c, closed: true }, hi: { value: c, closed: true } },
      ];
  }
}

function isEmptyInterval(i: Interval): boolean {
  if (i.lo === null || i.hi === null) {
    return false;
  }
  const order = compareR(i.lo.value, i.hi.value);
  return order > 0 || (order === 0 && !(i.lo.closed && i.hi.closed));
}

/** Order two lower ends: −∞ first, then by value, closed before open at a tie. */
function compareLo(a: Bound | null, b: Bound | null): number {
  if (a === null || b === null) {
    if (a === b) {
      return 0;
    }
    return a === null ? -1 : 1;
  }
  const order = compareR(a.value, b.value);
  if (order !== 0) {
    return order;
  }
  if (a.closed === b.closed) {
    return 0;
  }
  return a.closed ? -1 : 1;
}

/** Order two upper ends: +∞ last, then by value, open before closed at a tie. */
function compareHi(a: Bound | null, b: Bound | null): number {
  if (a === null || b === null) {
    if (a === b) {
      return 0;
    }
    return a === null ? 1 : -1;
  }
  const order = compareR(a.value, b.value);
  if (order !== 0) {
    return order;
  }
  if (a.closed === b.closed) {
    return 0;
  }
  return a.closed ? 1 : -1;
}

/** Do `a` (earlier) and `b` overlap, or touch so that no point is left between? */
function joins(a: Interval, b: Interval): boolean {
  if (a.hi === null || b.lo === null) {
    return true;
  }
  const order = compareR(a.hi.value, b.lo.value);
  return order > 0 || (order === 0 && (a.hi.closed || b.lo.closed));
}

export function normalizeSet(set: IntervalSet): IntervalSet {
  const sorted = set
    .filter((interval) => !isEmptyInterval(interval))
    .sort((a, b) => compareLo(a.lo, b.lo));
  const merged: IntervalSet = [];
  for (const interval of sorted) {
    const last = merged.at(-1);
    if (last && joins(last, interval)) {
      merged[merged.length - 1] = {
        lo: last.lo,
        hi: compareHi(last.hi, interval.hi) >= 0 ? last.hi : interval.hi,
      };
    } else {
      merged.push(interval);
    }
  }
  return merged;
}

export function unionSets(a: IntervalSet, b: IntervalSet): IntervalSet {
  return normalizeSet([...a, ...b]);
}

export function intersectSets(a: IntervalSet, b: IntervalSet): IntervalSet {
  const result: IntervalSet = [];
  for (const x of a) {
    for (const y of b) {
      result.push({
        lo: compareLo(x.lo, y.lo) >= 0 ? x.lo : y.lo,
        hi: compareHi(x.hi, y.hi) <= 0 ? x.hi : y.hi,
      });
    }
  }
  return normalizeSet(result);
}

export function isEmptySet(set: IntervalSet): boolean {
  return set.length === 0;
}

export function isAllReals(set: IntervalSet): boolean {
  return set.length === 1 && set[0].lo === null && set[0].hi === null;
}

export function setContains(set: IntervalSet, x: Rational): boolean {
  return set.some((interval) => {
    const aboveLo =
      interval.lo === null ||
      compareR(x, interval.lo.value) > 0 ||
      (interval.lo.closed && equalsR(x, interval.lo.value));
    const belowHi =
      interval.hi === null ||
      compareR(x, interval.hi.value) < 0 ||
      (interval.hi.closed && equalsR(x, interval.hi.value));
    return aboveLo && belowHi;
  });
}

export const EMPTY_SYMBOL = "∅";

function endText(bound: Bound, style: NumberStyle, mark: string): string {
  return formatStyled(bound.value, style, mark);
}

/** Interval notation: `(−∞, 3]`, `[−7/2, 3/2)`, `(−∞, −2) ∪ (1, ∞)`, `∅`. */
export function formatIntervalSet(
  set: IntervalSet,
  style: NumberStyle = "fraction",
  mark = "."
): string {
  if (set.length === 0) {
    return EMPTY_SYMBOL;
  }
  return set
    .map((interval) => {
      if (
        interval.lo &&
        interval.hi &&
        equalsR(interval.lo.value, interval.hi.value)
      ) {
        return `{${endText(interval.lo, style, mark)}}`;
      }
      const lo = interval.lo
        ? `${interval.lo.closed ? "[" : "("}${endText(interval.lo, style, mark)}`
        : "(−∞";
      const hi = interval.hi
        ? `${endText(interval.hi, style, mark)}${interval.hi.closed ? "]" : ")"}`
        : "∞)";
      return `${lo}, ${hi}`;
    })
    .join(" ∪ ");
}

/** One interval as a condition on the variable: `x < 3`, `−2 ≤ x < 5`, `x = 4`. */
export function conditionText(
  interval: Interval,
  variable: string,
  style: NumberStyle = "fraction",
  mark = "."
): string {
  const { lo, hi } = interval;
  if (lo && hi && equalsR(lo.value, hi.value)) {
    return `${variable} = ${endText(lo, style, mark)}`;
  }
  if (lo && hi) {
    return `${endText(lo, style, mark)} ${lo.closed ? "≤" : "<"} ${variable} ${hi.closed ? "≤" : "<"} ${endText(hi, style, mark)}`;
  }
  if (lo) {
    return `${variable} ${lo.closed ? "≥" : ">"} ${endText(lo, style, mark)}`;
  }
  if (hi) {
    return `${variable} ${hi.closed ? "≤" : "<"} ${endText(hi, style, mark)}`;
  }
  return variable;
}

export function formatFractionBound(bound: Bound): string {
  return formatFraction(bound.value);
}
